import {
    isSameMonth,
    isSameQuarter,
    isSameYear,
    isSameDay,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    startOfWeek,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    endOfWeek,
    addDays,
    addMonths,
    addQuarters,
    addWeeks,
    addYears,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
    format as formatDateFns,
    isValid,
    Day,
} from 'date-fns';
import { DateResolution, PeriodPickerSettings } from '../../api/generated/data-contracts';
import { CellRenderInfo } from './components/RangePicker';
import { enUS } from 'date-fns/locale';
import { settings } from '../../context/network/http/QueryProvider/queries/UISettings';

export const getFormat = (info: CellRenderInfo, resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Month:
            return info.locale.fieldMonthFormat;
        case DateResolution.Quarter:
            return info.locale.fieldQuarterFormat;
        case DateResolution.Year:
            return info.locale.fieldYearFormat;
        case DateResolution.Week:
        case DateResolution.Day:
        default:
            return info.locale.fieldDateFormat;
    }
};

export const getSameComparitor = (resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Month:
            return isSameMonth;
        case DateResolution.Quarter:
            return isSameQuarter;
        case DateResolution.Year:
            return isSameYear;
        case DateResolution.Week:
        case DateResolution.Day:
        default:
            return isSameDay;
    }
};

const getStartOfFn = (resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Month:
            return startOfMonth;
        case DateResolution.Quarter:
            return startOfQuarter;
        case DateResolution.Year:
            return startOfYear;
        case DateResolution.Week:
            return (date: Date) => {
                const uiSettings = settings();
                return startOfWeek(date, {
                    weekStartsOn: (uiSettings?.useISO8601 ? 1 : 7) as Day,
                });
            };
        case DateResolution.Day:
        default:
            return removeTime;
    }
};

export const getStartOf = (resolution: DateResolution, date: Date) => {
    return removeTime(getStartOfFn(resolution)(date));
};

const getEndOfFn = (resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Month:
            return endOfMonth;
        case DateResolution.Quarter:
            return endOfQuarter;
        case DateResolution.Year:
            return endOfYear;
        case DateResolution.Week: {
            return (date: Date) => {
                const uiSettings = settings();
                return endOfWeek(date, {
                    weekStartsOn: (uiSettings?.useISO8601 ? 1 : 7) as Day,
                });
            };
        }
        case DateResolution.Day:
        default:
            return removeTime;
    }
};

export const getEndOf = (resolution: DateResolution, date: Date) => {
    return removeTime(getEndOfFn(resolution)(date));
};

export const constrainDate = (date: Date, minDate: Date, maxDate: Date): Date => {
    if (date.getTime() < minDate.getTime()) {
        return minDate;
    }
    if (date.getTime() > maxDate.getTime()) {
        return maxDate;
    }
    return date;
};

const getAdder = (resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Day:
            return addDays;
        case DateResolution.Quarter:
            return addQuarters;
        case DateResolution.Week:
            return addWeeks;
        case DateResolution.Year:
            return addYears;
        case DateResolution.Month:
        default: {
            return addMonths;
        }
    }
};

export const addPeriod = (resolution: DateResolution, date: Date, periods: number) => {
    return getAdder(resolution)(date, periods);
};

const getSubber = (resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Day:
            return subDays;
        case DateResolution.Quarter:
            return subQuarters;
        case DateResolution.Week:
            return subWeeks;
        case DateResolution.Year:
            return subYears;
        case DateResolution.Month:
        default: {
            return subMonths;
        }
    }
};

export const subPeriod = (resolution: DateResolution, date: Date, periods: number) => {
    return getSubber(resolution)(date, periods);
};

export const getDefaultEndDate = (resolution: DateResolution, start: Date, periodPickerSettings: PeriodPickerSettings) => {
    return removeTime(getEndOf(resolution, addPeriod(resolution, start, getDefaultForResolution(resolution, periodPickerSettings).defaultSelectedUnits)));
};

export const getToday = () => {
    return removeTime();
};

export const removeTime = (date = new Date()) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
};

export const getDefaultForResolution = (resolution: DateResolution, periodPickerSettings: PeriodPickerSettings) => {
    const { day, month, quarter, week, year } = periodPickerSettings;
    switch (resolution) {
        case DateResolution.Day:
            return day;
        case DateResolution.Quarter:
            return quarter;
        case DateResolution.Week:
            return week;
        case DateResolution.Year:
            return year;
        case DateResolution.Month:
        default:
            return month;
    }
};

export const format = (date: Date, _format: string) => {
    if (!isValid(date)) {
        return null;
    }
    const uiSettings = settings();
    return formatDateFns(date, localeParse(_format), {
        locale: {
            ...enUS,
            options: {
                ...enUS.options,
                weekStartsOn: (uiSettings?.useISO8601 ? 1 : 7) as Day,
            },
        },
    });
};

export function localeParse(format: string) {
    // Year
    if (format === 'YYYY') {
        return 'yyyy';
    }
    // Quarter
    if (format === 'YYYY-[Q]Q') {
        return "'Q'Q yyyy";
    }
    // Week
    if (format === 'gggg-wo') {
        return "'W'w yyyy";
    }
    // Month
    if (format === 'YYYY-MM') {
        return 'MMM yyyy';
    }
    // Day
    if (format === 'YYYY-MM-DD') {
        return 'MMM d, yyyy';
    }
    return (
        format
            .replace(/Y/g, 'y')
            .replace(/D/g, 'd')
            .replace(/gggg/, 'yyyy')
            .replace(/g/g, 'G')
            .replace(/([Ww])o/g, 'wo')
            // .replace(/([Ww])o/g, '\'wW')
            // .replace(/\[Q\]Q/, "[Q]")
            // .replace(/\[Q\]Q/, "QQQ")
            .replace(/\[Q\]Q/, "'Q'Q")
    );
    // .replace(/\[Q\]Q/, "qqq")
}
